import { FunctionComponent } from 'react';
import { Moment } from 'moment-timezone';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { NewBookingAlert } from '@spike/new-booking-model';
import { AlertNewBooking } from '../UI/AlertNewBooking';
import { Option } from '@spike/model';
import { useMasterData } from 'hooks';
import { Switch } from 'components/UI';
import SelectAppointmentFrequency from './SelectAppointmentFrequency';
import PeriodSelectorSection from './DateSelectorSection/PeriodSelectorSection';
import styles from './PeriodSelector.module.scss';
import clsx from 'clsx';

interface PeriodSelectorProps {
    from: Moment;
    to: Moment;
    isRecurrent: boolean;
    duration?: Option<string>;
    frequency?: Option<string>;
    frequencyOptions: Array<Option<string>>;
    alerts?: Array<NewBookingAlert>;
    readOnly?: boolean;
    labelDate?: string;
    labelTimeStart?: string;
    labelTimeEnd?: string;
    hideEndTime?: boolean;
    onChangeFrequencyDuration: (duration: Option<string> | undefined, frequency: Option<string>) => void;
    onChangeRecurrent?: (isRecurrent: boolean) => void;
    onChangePeriod: (from: Moment, to: Moment) => void;
}

export const PeriodSelector: FunctionComponent<PeriodSelectorProps> = props => {
    const masterData = useMasterData();

    const onChangeFrequency = (value: Option<string> | undefined) => {
        const scheduleDuration = masterData.scheduleDuration.find(sd => sd.id === 'one_year');

        if (value !== undefined) {
            props.onChangeFrequencyDuration(scheduleDuration, value);
        }
    };

    return (
        <div
            className={clsx({
                [styles.recurrentContainer]: props.onChangeRecurrent,
                [styles.container]: !props.onChangeRecurrent
            })}
        >
            <div className={styles.dateSelectorContainer}>
                <PeriodSelectorSection
                    from={props.from}
                    to={props.to}
                    labelDate={props.labelDate}
                    labelTimeStart={props.labelTimeStart}
                    labelTimeEnd={props.labelTimeEnd}
                    hideEndTime={props.hideEndTime}
                    readOnly={props.readOnly}
                    className={clsx({
                        [styles.noRecurrent]: !props.onChangeRecurrent
                    })}
                    onSelectPeriod={props.onChangePeriod}
                />
                {props.onChangeRecurrent && (
                    <div className={styles.headerContainer}>
                        <span className={styles.recurring}>Recurring</span>
                        <Switch
                            id="booking_button_frequency"
                            checked={props.isRecurrent}
                            className={styles.switch}
                            onChange={() => props.onChangeRecurrent && props.onChangeRecurrent(!props.isRecurrent)}
                        />
                    </div>
                )}
            </div>

            {props.isRecurrent && (
                <SelectAppointmentFrequency
                    placeholder="Frequency"
                    options={props.frequencyOptions}
                    selectedOption={props.frequency}
                    className={styles.selectField}
                    onSelect={onChangeFrequency}
                />
            )}
            {props.alerts?.map(alert => (
                <div
                    key={`booking_div_${alert.alertType}_alert`}
                    id={`booking_div_${alert.alertType}_alert`}
                >
                    <AlertNewBooking
                        className={styles.alert}
                        bWidth="2px"
                        icon={faExclamationTriangle}
                        iconSize="lg"
                        iconColor="#EAB464"
                        bgColor="#F8F5F1"
                        bdColor="#BCB8AE"
                    >
                        <span className={styles.alertText}>{alert.message}</span>
                    </AlertNewBooking>
                </div>
            ))}
        </div>
    );
};

export default PeriodSelector;
